import React, { useCallback, useMemo } from 'react';

import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import { ALERT_TYPES, AlertSeverity } from '@bringg/types';
import { FilterBar2 } from '@bringg/react-components';
import type { FilterBar2ListItem, FilterBar2Value, FilterBar2Values } from '@bringg/react-components';

import { AlertOwnerEnum, ExceptionFilters, TaskAssignedStateEnum } from 'bringg-web/types/common.consts';
import { useStores } from '../../../../recipes';
import { filterBar2Translations } from '../../../filter-bar/filter-bar';
import { mapTeamToFilter } from '../../../filter-bar/fliter-bar-utils';
import useAlertOptions from './use-alert-options';
import type { ExceptionRoute } from 'bringg-web/types/common.consts';
import type { TableConfigObjectAdapter } from 'bringg-web/stores/alerts-view/table-config-object-adapter';
import type { AdditionalFilters } from 'bringg-web/stores/alerts-view/table-config-utils';

interface Props {
	tableConfig: TableConfigObjectAdapter;
	alertTypes?: ALERT_TYPES[];
	availableRoutes?: ExceptionRoute[];
}

function FiltersPanel({ tableConfig, alertTypes, availableRoutes }: Props) {
	const { t } = useTranslation();
	const {
		tableFilters: filtersValue,
		setNewTableFilters,
		resetTableFilters,
		additionalFiltersNames,
		setAdditionalTableFiltersNames
	} = tableConfig;

	const {
		teamsStore,
		companiesStore: { getAll: allCompanies },
		alertsStore
	} = useStores();

	const filterBarTranslations = useMemo(() => filterBar2Translations(t), [t]);

	const alertsOptions = useAlertOptions(alertTypes, tableConfig.tableId);

	const teamOptions = useMemo<FilterBar2Value[]>(
		() => mapTeamToFilter(teamsStore.all, teamsStore),
		[teamsStore, teamsStore.all]
	);
	const companiesOptions = useMemo<FilterBar2Value[]>(
		() => allCompanies.map(({ id, name }) => ({ value: id, label: name })),
		[allCompanies]
	);
	// TODO: Create a guard useEffect for options which inferred from exceptions
	// as it is possible to have those values in filters as they are not present in options anymore

	const assignedStateOptions = useMemo(
		() => [
			{ value: TaskAssignedStateEnum.ALL, label: t('EXCEPTIONS_LIST.ALL_TASKS') },
			{ value: TaskAssignedStateEnum.ASSIGNED, label: t('EXCEPTIONS_LIST.ASSIGNED_TASKS') },
			{ value: TaskAssignedStateEnum.UNASSIGNED, label: t('EXCEPTIONS_LIST.UNASSIGNED_TASKS') }
		],
		[t]
	);

	const ownersOptions = useMemo(
		() => [
			{ value: AlertOwnerEnum.CURRENT_USER, label: t('EXCEPTIONS_LIST.OWNED_BY_CURRENT_USER') },
			{ value: AlertOwnerEnum.NO_OWNER, label: t('EXCEPTIONS_LIST.OWNED_BY_NO_OWNER') },
			{ value: AlertOwnerEnum.OTHER, label: t('EXCEPTIONS_LIST.OWNED_BY_OTHER') }
		],
		[t]
	);

	const severityOptions = useMemo(
		() => [
			{ value: AlertSeverity.Low, label: t('ALERTS_CONFIGURATION.SEVERITY_OPTION.LOW') },
			{ value: AlertSeverity.Medium, label: t('ALERTS_CONFIGURATION.SEVERITY_OPTION.MEDIUM') },
			{ value: AlertSeverity.High, label: t('ALERTS_CONFIGURATION.SEVERITY_OPTION.HIGH') }
		],
		[t]
	);

	const routeOptions = useMemo<FilterBar2Value[]>(() => {
		if (!availableRoutes?.length) {
			return [];
		}

		const routes = availableRoutes.reduce((acc, { routeId, routeTitle }) => {
			acc.set(routeId, routeTitle);

			return acc;
		}, new Map<number, string>());

		return Array.from(routes, ([routeId, routeTitle]) => {
			return {
				value: routeId,
				label: routeTitle
			};
		});
	}, [availableRoutes]);

	const handleReset = useCallback((field?: string) => {
		resetTableFilters(field as keyof ExceptionFilters);
	}, []);

	const handleFiltersChanged = useCallback((newFilters: FilterBar2Values) => {
		setNewTableFilters(newFilters);
	}, []);

	const handleAdditionalAdded = useCallback(
		(keys: string[]) => setAdditionalTableFiltersNames(keys as AdditionalFilters),
		[]
	);

	const filterInputs: FilterBar2ListItem[] = useMemo(() => {
		const inputs: FilterBar2ListItem[] = [
			{
				type: FilterBar2.TYPES.SEARCH,
				applyFilterOnValueChange: true,
				name: 'searchText',
				placeholder: `${t('EXCEPTIONS_LIST.SEARCH')} ${t('EXCEPTIONS_LIST.ID')}`
			},
			{
				type: FilterBar2.TYPES.TEAMS,
				multiselect: true,
				applyFilterOnValueChange: true,
				name: 'teams',
				title: t(`EXCEPTIONS_LIST.TEAMS_FILTER`),
				values: teamOptions
			},
			{
				type: FilterBar2.TYPES.SELECT,
				multiselect: true,
				applyFilterOnValueChange: true,
				name: 'alerts',
				title: t(`EXCEPTIONS_LIST.ALERTS_FILTER`),
				values: alertsOptions
			},
			{
				type: FilterBar2.TYPES.SELECT,
				applyFilterOnValueChange: true,
				name: 'alertOwner',
				title: t(`EXCEPTIONS_LIST.ALERT_OWNER_FILTER`),
				values: ownersOptions
			},
			{
				type: FilterBar2.TYPES.SELECT,
				multiselect: true,
				additional: true,
				applyFilterOnValueChange: true,
				name: 'companies',
				title: t(`EXCEPTIONS_LIST.COMPANIES_FILTER`),
				values: companiesOptions
			},
			{
				type: FilterBar2.TYPES.SELECT,
				applyFilterOnValueChange: true,
				name: 'severity',
				title: t('ALERTS_CONFIGURATION.SEVERITY'),
				values: severityOptions
			}
		];

		if (filtersValue.routes) {
			inputs.push({
				type: FilterBar2.TYPES.SELECT,
				multiselect: true,
				applyFilterOnValueChange: true,
				name: 'routes',
				loading: !alertsStore.isAllTaskAlertsDataLoaded,
				title: t(`EXCEPTIONS_LIST.ROUTES_FILTER`),
				values: routeOptions
			});
		}
		if (Object.prototype.hasOwnProperty.call(filtersValue, 'assignedState')) {
			inputs.push({
				type: FilterBar2.TYPES.RADIO,
				applyFilterOnValueChange: true,
				name: 'assignedState',
				title: t(`EXCEPTIONS_LIST.ASSIGNED_STATE_FILTER`),
				values: assignedStateOptions
			});
		}

		return inputs;
	}, [
		teamOptions,
		alertsOptions,
		ownersOptions,
		routeOptions,
		assignedStateOptions,
		filtersValue,
		companiesOptions,
		alertsStore.isAllTaskAlertsDataLoaded
	]);

	return (
		<div className="exceptions-filters-panel">
			<FilterBar2
				className="alerts-table-filter-bar"
				translations={filterBarTranslations}
				initialValues={filtersValue as unknown as FilterBar2Values}
				initialAdditional={additionalFiltersNames}
				list={filterInputs}
				onReset={handleReset}
				onAdditional={handleAdditionalAdded}
				// be aware the way apply whole new obj to filter observer maybe will rerender the whole filterbar
				onFilter={handleFiltersChanged}
			/>
		</div>
	);
}

export default observer(FiltersPanel) as typeof FiltersPanel;
